"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import CheckIcon from "../../icon/CheckIcon.js";
import CloseIcon from "../../icon/CloseIcon.js";
import HelpIcon from "../../icon/HelpIcon.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import type { Toast } from "./Toast.js";
import { type ToastType } from "./Toast.js";


const iconColourClasses: Record<ToastType, string | null> = {
    success: "text-emphasis",
    error: "text-danger",
    warning: "text-danger",
    info: null,
    achievement: "text-accent",
};

function getAriaLive(type: ToastType): "polite" | "assertive" {
    return type === "error" || type === "warning" ? "assertive" : "polite";
}

function getAriaRole(type: ToastType): "status" | "alert" {
    return type === "error" || type === "warning" ? "alert" : "status";
}

interface ToastElemProps {
    toast: Toast;
    onDismiss: (id: string) => void;
}

export function ToastElem({ toast, onDismiss }: ToastElemProps) {
    const {
        id: toastID,
        options: toastOptions,
    } = toast;

    const {
        type: toastType,
        icon: toastIcon,
        title,
        message,
        description,
        includeBorder,
    } = toastOptions;

    const [isFirstShow, setIsFirstShow] = useState(false);

    // Pause/resume on unmount/mount.
    useEffect(() => {
        toast.resumeTimer();
        return () => toast.pauseTimer();
    }, [toast]);

    useEffect(() => {
        let timeoutID = setTimeout(() => {
            setIsFirstShow(toast.maybeTriggerOnVisible());
            timeoutID = setTimeout(() => setIsFirstShow(false), 750);
        });
        return () => clearTimeout(timeoutID);
    }, [toast]);

    const handleDismiss = useCallback(() => {
        onDismiss(toastID);
    }, [toastID, onDismiss]);

    const isAchievement = toastType === "achievement";
    const icon = useMemo(() => {
        if (toastIcon)
            return toastIcon;

        const iconClassName = cn("relative size-full", iconColourClasses[toastType]);
        switch (toastType) {
            case "success":
                return (<CheckIcon className={iconClassName} />);
            case "error":
                return (<CloseIcon className={iconClassName} />);
            case "info":
                return (<HelpIcon className={iconClassName} />);
            case "warning":
                return (<span className={iconClassName}>⚠</span>);
            case "achievement":
                return (<span className={iconClassName}>🏆</span>);
            default:
                return null;
        }
    }, [toastIcon, toastType]);

    return (
        <div
            className={cn(
                "relative flex w-[24rem] max-w-full items-center gap-4 overflow-hidden rounded-sm shadow-md",
                "transition-[translate,box-shadow] duration-200 ease-[ease] hover:-translate-y-[2px] hover:shadow-lg",
                isAchievement ? "bg-surface-sunken p-2 text-strong" : "p-3",
                includeBorder ? "border-3 border-accent" : null,
                isAchievement && isFirstShow ? "animate-achievement-flash" : null,
            )}
            role={getAriaRole(toastType)}
            aria-live={getAriaLive(toastType)}
            onMouseEnter={() => toast.pauseTimer()}
            onMouseLeave={() => toast.resumeTimer()}
            onClick={handleDismiss}
            style={{ cursor: "pointer" }}>

            {icon && (
                <div className={cn(
                    "flex shrink-0 items-center justify-center",
                    isAchievement ? "size-[4.5rem]" : "size-[4rem]",
                )}>
                    {icon}
                </div>
            )}
            <div className="min-w-0 flex-1">
                <div className="m-0 flex flex-col">
                    {title && (
                        <span className={cn("text-xs wrap-break-word", isAchievement ? "text-accent" : null)}>
                            {title}
                        </span>
                    )}
                    <span className={cn(
                        "wrap-break-word",
                        isAchievement ? "text-xl font-bold text-strong" : "text-sm",
                    )}>
                        {message}
                    </span>
                    {description && (
                        <span className={cn(
                            "text-sm wrap-break-word",
                            isAchievement ? "text-body" : null,
                        )}>
                            {description}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
